import { useEffect, useState, useCallback, useRef } from 'react';
import {
  Activity,
  BarChart3,
  Clock,
  TrendingUp,
  User,
  RefreshCw,
  Calendar,
  Eye,
  X,
  Copy,
  Check,
  ChevronDown,
  ChevronUp,
  Filter,
  ChevronLeft,
  ChevronRight,
  Search,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  useNodeTrackingData,
  useNodeTrackingStats,
  useNodeTrackingFilterOptions,
  type NodeTrackingRecord,
  type NodeTrackingFilters,
} from '@/hooks/useNodeTracking';
import { toast } from 'sonner';

// ─── helpers ─────────────────────────────────────────────────────────────────

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function formatRelativeTime(dateString: string): string {
  const now = new Date();
  const date = new Date(dateString);
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
}

function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

// ─── Searchable combobox ──────────────────────────────────────────────────────

interface SearchableSelectProps {
  value: string;
  onChange: (v: string) => void;
  options: string[];
  placeholder: string;
  allLabel: string;
}

function SearchableSelect({ value, onChange, options, placeholder, allLabel }: SearchableSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handle(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  const filtered = query ? options.filter((o) => o.toLowerCase().includes(query.toLowerCase())) : options;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => {
          setOpen((v) => !v);
          setQuery('');
        }}
        className={[
          'w-full flex items-center justify-between gap-2 px-3 py-1.5 text-sm rounded-md border',
          'border-black/[0.12] dark:border-white/[0.12] bg-transparent',
          'text-black/85 dark:text-white/85 focus:outline-none focus:ring-2 focus:ring-blue-500/40',
          'hover:bg-black/[0.02] dark:hover:bg-white/[0.02]',
          value ? 'border-blue-400 dark:border-blue-400' : '',
        ].join(' ')}
      >
        <span className={`truncate ${!value ? 'text-black/40 dark:text-white/40' : ''}`}>{value || allLabel}</span>
        <div className="flex items-center gap-1 shrink-0">
          {value && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                onChange('');
                setOpen(false);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.stopPropagation();
                  onChange('');
                  setOpen(false);
                }
              }}
              className="text-black/40 hover:text-black/70 dark:text-white/40 dark:hover:text-white/70"
            >
              <X className="w-3 h-3" />
            </span>
          )}
          <ChevronDown className="w-3.5 h-3.5 text-black/40 dark:text-white/40" />
        </div>
      </button>

      {open && (
        <div
          className="absolute z-50 mt-1 w-full bg-white dark:bg-neutral-800 border
            border-black/[0.12] dark:border-white/[0.12] rounded-md shadow-lg overflow-hidden"
        >
          <div className="flex items-center gap-2 px-2 py-1.5 border-b border-black/[0.06] dark:border-white/[0.06]">
            <Search className="w-3.5 h-3.5 text-black/40 dark:text-white/40 shrink-0" />
            <input
              autoFocus
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={placeholder}
              className={[
                'flex-1 text-sm bg-transparent focus:outline-none',
                'text-black/85 dark:text-white/85',
                'placeholder-black/40 dark:placeholder-white/40',
              ].join(' ')}
            />
            {query && (
              <button onClick={() => setQuery('')} className="text-black/40 hover:text-black/70 dark:text-white/40">
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
          <div className="max-h-52 overflow-y-auto">
            <button
              type="button"
              onClick={() => {
                onChange('');
                setOpen(false);
              }}
              className={`w-full text-left px-3 py-1.5 text-sm hover:bg-black/[0.04] dark:hover:bg-white/[0.04]
                ${!value ? 'font-medium text-blue-600 dark:text-blue-400' : 'text-black/60 dark:text-white/60'}`}
            >
              {allLabel}
            </button>
            {filtered.length === 0 ? (
              <p className="px-3 py-2 text-xs text-black/40 dark:text-white/40">No results</p>
            ) : (
              filtered.map((opt) => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => {
                    onChange(opt);
                    setOpen(false);
                    setQuery('');
                  }}
                  className={`w-full text-left px-3 py-1.5 text-sm hover:bg-black/[0.04] dark:hover:bg-white/[0.04]
                    ${value === opt ? 'font-medium text-blue-600 dark:text-blue-400' : 'text-black/85 dark:text-white/85'}`}
                >
                  {opt}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Mini date-range picker ───────────────────────────────────────────────────

interface DateRange {
  start: string; // YYYY-MM-DD
  end: string; // YYYY-MM-DD
}

interface DateRangePickerProps {
  value: DateRange | null;
  onChange: (v: DateRange | null) => void;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

function toYMD(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function DateRangePicker({ value, onChange }: DateRangePickerProps) {
  const [open, setOpen] = useState(false);
  const [viewYear, setViewYear] = useState(() => new Date().getFullYear());
  const [viewMonth, setViewMonth] = useState(() => new Date().getMonth());
  // selecting = first click done, waiting for second
  const [selecting, setSelecting] = useState<string | null>(null);
  const [hoverDay, setHoverDay] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handle(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setSelecting(null);
      }
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  const today = toYMD(new Date());

  function prevMonth() {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear((y) => y - 1);
    } else {
      setViewMonth((m) => m - 1);
    }
  }

  function nextMonth() {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear((y) => y + 1);
    } else {
      setViewMonth((m) => m + 1);
    }
  }

  // All days in the grid (pad with nulls)
  const firstDay = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const cells: (string | null)[] = [
    ...Array(firstDay).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => {
      const d = i + 1;
      return `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    }),
  ];
  // Pad to full weeks
  while (cells.length % 7 !== 0) cells.push(null);

  function handleDayClick(day: string) {
    if (!selecting) {
      // First click — start of range
      setSelecting(day);
    } else {
      // Second click — end of range
      const start = selecting <= day ? selecting : day;
      const end = selecting <= day ? day : selecting;
      onChange({ start, end });
      setSelecting(null);
      setOpen(false);
    }
  }

  function isDayInRange(day: string): boolean {
    if (selecting && hoverDay) {
      const lo = selecting <= hoverDay ? selecting : hoverDay;
      const hi = selecting <= hoverDay ? hoverDay : selecting;
      return day >= lo && day <= hi;
    }
    if (value) return day >= value.start && day <= value.end;
    return false;
  }

  function isDayStart(day: string) {
    if (selecting) return day === selecting;
    return value ? day === value.start : false;
  }

  function isDayEnd(day: string) {
    if (selecting && hoverDay) {
      return day === (selecting <= hoverDay ? hoverDay : selecting);
    }
    return value ? day === value.end : false;
  }

  const displayLabel = value ? (value.start === value.end ? value.start : `${value.start} → ${value.end}`) : 'All dates';

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => {
          setOpen((v) => !v);
          setSelecting(null);
        }}
        className={`w-full flex items-center justify-between gap-2 px-3 py-1.5 text-sm rounded-md border
          border-black/[0.12] dark:border-white/[0.12] bg-transparent
          text-black/85 dark:text-white/85 focus:outline-none focus:ring-2 focus:ring-blue-500/40
          hover:bg-black/[0.02] dark:hover:bg-white/[0.02] ${value ? 'border-blue-400 dark:border-blue-400' : ''}`}
      >
        <div className="flex items-center gap-1.5 truncate">
          <Calendar className="w-3.5 h-3.5 shrink-0 text-black/50 dark:text-white/50" />
          <span className={`truncate ${!value ? 'text-black/40 dark:text-white/40' : ''}`}>{displayLabel}</span>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {value && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                onChange(null);
                setOpen(false);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.stopPropagation();
                  onChange(null);
                  setOpen(false);
                }
              }}
              className="text-black/40 hover:text-black/70 dark:text-white/40 dark:hover:text-white/70"
            >
              <X className="w-3 h-3" />
            </span>
          )}
          <ChevronDown className="w-3.5 h-3.5 text-black/40 dark:text-white/40" />
        </div>
      </button>

      {open && (
        <div
          className="absolute z-50 mt-1 left-0 bg-white dark:bg-neutral-800 border
            border-black/[0.12] dark:border-white/[0.12] rounded-lg shadow-xl p-3 min-w-[260px]"
        >
          {/* Month navigation */}
          <div className="flex items-center justify-between mb-2">
            <button
              onClick={prevMonth}
              className="p-1 rounded hover:bg-black/[0.05] dark:hover:bg-white/[0.05] text-black/60 dark:text-white/60"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-sm font-medium text-black/80 dark:text-white/80">
              {MONTHS[viewMonth]} {viewYear}
            </span>
            <button
              onClick={nextMonth}
              className="p-1 rounded hover:bg-black/[0.05] dark:hover:bg-white/[0.05] text-black/60 dark:text-white/60"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          {/* Day headers */}
          <div className="grid grid-cols-7 mb-1">
            {DAYS.map((d) => (
              <div key={d} className="text-center text-[10px] font-medium text-black/40 dark:text-white/40 py-0.5">
                {d}
              </div>
            ))}
          </div>

          {/* Day cells */}
          <div className="grid grid-cols-7 gap-y-0.5">
            {cells.map((day, i) => {
              if (!day) return <div key={`empty-${i}`} />;
              const inRange = isDayInRange(day);
              const isStart = isDayStart(day);
              const isEnd = isDayEnd(day);
              const isToday = day === today;
              const isFuture = day > today;
              return (
                <button
                  key={day}
                  disabled={isFuture}
                  type="button"
                  onClick={() => handleDayClick(day)}
                  onMouseEnter={() => selecting && setHoverDay(day)}
                  onMouseLeave={() => setHoverDay(null)}
                  className={`relative text-center text-xs py-1 transition-colors
                    ${isFuture ? 'opacity-30 cursor-not-allowed' : 'cursor-pointer'}
                    ${inRange && !isStart && !isEnd ? 'bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-300' : ''}
                    ${isStart || isEnd ? 'bg-blue-500 text-white rounded-md font-semibold' : ''}
                    ${
                      !inRange && !isStart && !isEnd
                        ? 'hover:bg-black/[0.04] dark:hover:bg-white/[0.04] text-black/80 dark:text-white/80 rounded-md'
                        : ''
                    }
                    ${isToday && !isStart && !isEnd ? 'font-bold underline' : ''}
                  `}
                >
                  {parseInt(day.split('-')[2], 10)}
                </button>
              );
            })}
          </div>

          {/* Quick presets */}
          <div className="mt-2 pt-2 border-t border-black/[0.06] dark:border-white/[0.06] flex flex-wrap gap-1">
            {[
              { label: 'Today', fn: () => ({ start: today, end: today }) },
              {
                label: 'Last 7d',
                fn: () => {
                  const d = new Date();
                  d.setDate(d.getDate() - 6);
                  return { start: toYMD(d), end: today };
                },
              },
              {
                label: 'Last 30d',
                fn: () => {
                  const d = new Date();
                  d.setDate(d.getDate() - 29);
                  return { start: toYMD(d), end: today };
                },
              },
              {
                label: 'This month',
                fn: () => {
                  const d = new Date();
                  return {
                    start: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`,
                    end: today,
                  };
                },
              },
            ].map(({ label, fn }) => (
              <button
                key={label}
                type="button"
                onClick={() => {
                  onChange(fn());
                  setOpen(false);
                  setSelecting(null);
                }}
                className="text-[11px] px-2 py-0.5 rounded border border-black/[0.1] dark:border-white/[0.1]
                  hover:bg-black/[0.04] dark:hover:bg-white/[0.04] text-black/60 dark:text-white/60"
              >
                {label}
              </button>
            ))}
          </div>

          {selecting && (
            <p className="mt-1 text-[11px] text-blue-600 dark:text-blue-400 text-center">
              Click a second date to complete the range
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ─── component ───────────────────────────────────────────────────────────────

// Convert a DateRange to a BigQuery-compatible time range string used by the backend.
// We pass it as a custom ISO string pair via the `timeRange` field with a special prefix.
// Backend already handles '1h', '24h', '7d', '30d', '90d'. For date ranges we fall back
// to '90d' and rely on the cursor WHERE clause to further restrict. Actually we pass
// a custom format and update the backend to handle date ranges as ISO pairs.
// For simplicity, for a date range we encode it as `date:YYYY-MM-DD:YYYY-MM-DD`.
function dateRangeToTimeRange(dr: DateRange): string {
  return `date:${dr.start}:${dr.end}`;
}

export default function NodeTracking() {
  const [timeRange, setTimeRange] = useState('7d');
  const [selectedPayload, setSelectedPayload] = useState<unknown>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [hoveredBar, setHoveredBar] = useState<{ hour: string; count: number; x: number; y: number } | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);

  // ── filter state ────────────────────────────────────────────────────────────
  const [filterUserEmail, setFilterUserEmail] = useState('');
  const [filterClassType, setFilterClassType] = useState('');
  const [filterModel, setFilterModel] = useState('');
  // null = use global time range selector
  const [tableDateRange, setTableDateRange] = useState<DateRange | null>(null);

  const effectiveTableTimeRange = tableDateRange ? dateRangeToTimeRange(tableDateRange) : timeRange;

  const debouncedUserEmail = useDebounce(filterUserEmail, 400);

  const activeFilters: NodeTrackingFilters = {
    userEmail: debouncedUserEmail || undefined,
    classType: filterClassType || undefined,
    model: filterModel || undefined,
  };
  const hasActiveFilters = !!(debouncedUserEmail || filterClassType || filterModel);
  const hasTableDateRange = tableDateRange !== null;

  // ── cursor stack ──────────────────────────────────────────────────────────
  const [cursorStack, setCursorStack] = useState<(string | undefined)[]>([undefined]);
  const canGoBack = cursorStack.length > 1;
  const currentPage = cursorStack.length;

  // Ref to scroll the table into view on page changes
  const tableSectionRef = useRef<HTMLDivElement>(null);

  function scrollToTable() {
    tableSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  // ── hooks ─────────────────────────────────────────────────────────────────
  const {
    data: records,
    total,
    hasNextPage,
    nextCursor,
    loading: recordsLoading,
    error: recordsError,
    fetchData,
  } = useNodeTrackingData();
  const { stats, loading: statsLoading, error: statsError, fetchStats } = useNodeTrackingStats();
  const { options: filterOptions, fetchOptions } = useNodeTrackingFilterOptions();

  // statsLoading causes the full-page spinner — keep it separate
  const error = recordsError || statsError;

  // Track whether the initial stats+records load has completed at least once
  const initialLoadDone = useRef(false);
  const statsRef = useRef(false);

  const activeFiltersRef = useRef(activeFilters);
  activeFiltersRef.current = activeFilters;

  const fetchTablePage = useCallback(
    (cursor: string | undefined, tr: string) => {
      fetchData(tr, cursor, activeFiltersRef.current);
    },
    [fetchData],
  );

  // Table refetch on filter/date changes
  useEffect(() => {
    setCursorStack([undefined]);
    fetchTablePage(undefined, effectiveTableTimeRange);
  }, [effectiveTableTimeRange, debouncedUserEmail, filterClassType, filterModel]); // fetchTablePage is stable (useCallback)

  // Stats + filter options — only on global time range change
  useEffect(() => {
    if (!statsRef.current) {
      statsRef.current = true;
      fetchStats(timeRange).then(() => {
        initialLoadDone.current = true;
      });
      fetchOptions();
      return;
    }
    fetchStats(timeRange);
  }, [timeRange]); // fetchStats/fetchOptions are stable (useCallback)

  const refreshData = useCallback(async () => {
    setCursorStack([undefined]);
    await Promise.all([fetchTablePage(undefined, effectiveTableTimeRange), fetchStats(timeRange)]);
  }, [effectiveTableTimeRange, timeRange, fetchTablePage, fetchStats]);

  function goNext() {
    if (!nextCursor) return;
    const newStack = [...cursorStack, nextCursor];
    setCursorStack(newStack);
    fetchTablePage(nextCursor, effectiveTableTimeRange);
    scrollToTable();
  }

  function goBack() {
    if (!canGoBack) return;
    const newStack = cursorStack.slice(0, -1);
    setCursorStack(newStack);
    const prevCursor = newStack[newStack.length - 1];
    fetchTablePage(prevCursor, effectiveTableTimeRange);
    scrollToTable();
  }

  function clearFilters() {
    setFilterUserEmail('');
    setFilterClassType('');
    setFilterModel('');
    setTableDateRange(null);
  }

  function copyJson(json: string, key: string) {
    navigator.clipboard.writeText(json).then(() => {
      setCopiedKey(key);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopiedKey(null), 2000);
    });
  }

  // ── early return: only for true initial load (stats haven't arrived yet) ────
  if (statsLoading && !initialLoadDone.current) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-12 text-center">
        <p className="text-red-600 text-sm mb-2">Error loading node tracking data</p>
        <p className="text-red-500 text-xs">{error}</p>
      </div>
    );
  }

  const maxHourlyCount = Math.max(...stats.hourlyActivity.map((h: { count: number }) => h.count));
  const currentHour = new Date().getHours();
  const reorderedHourlyActivity = [
    ...stats.hourlyActivity.slice(currentHour + 1),
    ...stats.hourlyActivity.slice(0, currentHour + 1),
  ];

  const activeFilterCount = [debouncedUserEmail, filterClassType, filterModel, hasTableDateRange ? '1' : ''].filter(
    Boolean,
  ).length;

  // ─── render ──────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Controls row */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-black/60 dark:text-white/60" />
            <span className="text-sm text-black/60 dark:text-white/60 hidden xs:inline">Time Range:</span>
          </div>
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1h">Last Hour</SelectItem>
              <SelectItem value="24h">Last 24h</SelectItem>
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
              <SelectItem value="90d">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button
          onClick={refreshData}
          disabled={statsLoading}
          variant="outline"
          size="sm"
          className="flex items-center gap-2 w-full sm:w-auto"
        >
          <RefreshCw className={`w-4 h-4 ${statsLoading ? 'animate-spin' : ''}`} />
          <span className="sm:inline">Refresh Data</span>
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="rounded-lg border border-black/[0.06] dark:border-white/[0.06] bg-white dark:bg-neutral-800 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-black/60 dark:text-white/60">Total Executions</p>
              <p className="text-2xl font-semibold text-black/85 dark:text-white/85">{stats.totalExecutions.toLocaleString()}</p>
            </div>
            <div className="p-2 rounded-lg bg-blue-50 dark:bg-blue-500/20">
              <Activity className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
        </div>
        <div className="rounded-lg border border-black/[0.06] dark:border-white/[0.06] bg-white dark:bg-neutral-800 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-black/60 dark:text-white/60">Active Users</p>
              <p className="text-2xl font-semibold text-black/85 dark:text-white/85">{stats.uniqueUsers}</p>
            </div>
            <div className="p-2 rounded-lg bg-green-50 dark:bg-green-500/20">
              <User className="w-5 h-5 text-green-600 dark:text-green-400" />
            </div>
          </div>
        </div>
        <div className="rounded-lg border border-black/[0.06] dark:border-white/[0.06] bg-white dark:bg-neutral-800 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-black/60 dark:text-white/60">Unique Nodes</p>
              <p className="text-2xl font-semibold text-black/85 dark:text-white/85">{stats.uniqueNodes}</p>
            </div>
            <div className="p-2 rounded-lg bg-purple-50 dark:bg-purple-500/20">
              <BarChart3 className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
          </div>
        </div>
        <div className="rounded-lg border border-black/[0.06] dark:border-white/[0.06] bg-white dark:bg-neutral-800 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-black/60 dark:text-white/60">Avg/Hour</p>
              <p className="text-2xl font-semibold text-black/85 dark:text-white/85">{Math.round(stats.totalExecutions / 24)}</p>
            </div>
            <div className="p-2 rounded-lg bg-orange-50 dark:bg-orange-500/20">
              <Clock className="w-5 h-5 text-orange-600 dark:text-orange-400" />
            </div>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="rounded-lg border border-black/[0.06] dark:border-white/[0.06] bg-white dark:bg-neutral-800 p-6">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-5 h-5 text-black/60 dark:text-white/60" />
            <h3 className="font-medium text-black/85 dark:text-white/85">Top 10 Most Used Nodes</h3>
          </div>
          <div className="space-y-3">
            {stats.topNodes.map((node: { class_type: string; count: number }, index: number) => {
              const percentage = Math.round((node.count / stats.totalExecutions) * 100);
              return (
                <div key={node.class_type} className="flex items-center gap-3">
                  <div className="w-6 text-xs text-black/50 dark:text-white/50 text-center">{index + 1}</div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-black/70 dark:text-white/70">{node.class_type}</span>
                      <span className="text-xs text-black/50 dark:text-white/50">
                        {node.count} ({percentage}%)
                      </span>
                    </div>
                    <div className="w-full bg-black/[0.05] dark:bg-white/[0.1] rounded-full h-2">
                      <div
                        className="bg-blue-500 dark:bg-blue-400 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="rounded-lg border border-black/[0.06] dark:border-white/[0.06] bg-white dark:bg-neutral-800 p-6">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-5 h-5 text-black/60 dark:text-white/60" />
            <h3 className="font-medium text-black/85 dark:text-white/85">Top 10 Most Used Models</h3>
          </div>
          <div className="space-y-3">
            {stats.topModels.map((node: { model: string; count: number }, index: number) => {
              const percentage = Math.round((node.count / stats.totalExecutions) * 100);
              return (
                <div key={node.model} className="flex items-center gap-3">
                  <div className="w-6 text-xs text-black/50 dark:text-white/50 text-center">{index + 1}</div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-black/70 dark:text-white/70">{node.model}</span>
                      <span className="text-xs text-black/50 dark:text-white/50">
                        {node.count} ({percentage}%)
                      </span>
                    </div>
                    <div className="w-full bg-black/[0.05] dark:bg-white/[0.1] rounded-full h-2">
                      <div
                        className="bg-blue-500 dark:bg-blue-400 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="rounded-lg border border-black/[0.06] dark:border-white/[0.06] bg-white dark:bg-neutral-800 p-6">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-5 h-5 text-black/60 dark:text-white/60" />
            <h3 className="font-medium text-black/85 dark:text-white/85">Top 5 Users</h3>
          </div>
          <div className="space-y-3">
            {stats.topUsers.map((node: { user_email: string; count: number }, index: number) => {
              const percentage = Math.round((node.count / stats.totalExecutions) * 100);
              return (
                <div key={node.user_email} className="flex items-center gap-3">
                  <div className="w-6 text-xs text-black/50 dark:text-white/50 text-center">{index + 1}</div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-black/70 dark:text-white/70">{node.user_email}</span>
                      <span className="text-xs text-black/50 dark:text-white/50">
                        {node.count} ({percentage}%)
                      </span>
                    </div>
                    <div className="w-full bg-black/[0.05] dark:bg-white/[0.1] rounded-full h-2">
                      <div
                        className="bg-blue-500 dark:bg-blue-400 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="rounded-lg border border-black/[0.06] dark:border-white/[0.06] bg-white dark:bg-neutral-800 p-6 pb-8">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="w-5 h-5 text-black/60 dark:text-white/60" />
            <h3 className="font-medium text-black/85 dark:text-white/85">Hourly Activity (Last 24h)</h3>
          </div>
          <div className="relative h-32 mb-6">
            <div className="flex items-end gap-1 h-full">
              {reorderedHourlyActivity.map((item: { hour: string; count: number }) => {
                const maxHeight = 100;
                const rawHeight = maxHourlyCount > 0 ? (item.count / maxHourlyCount) * maxHeight : 0;
                const height = item.count > 0 ? Math.max(rawHeight, 8) : 0;
                const hourRaw = item.hour.split(':')[0];
                const hourNumber = hourRaw === '00' ? '00' : parseInt(hourRaw, 10).toString();
                return (
                  <div key={item.hour} className="flex-1 flex flex-col items-center h-full">
                    <div className="flex-1 flex items-end justify-center relative w-full">
                      <div
                        className={`w-full max-w-[20px] rounded-t transition-all duration-500 cursor-pointer
                          ${item.count > 0 ? 'bg-blue-500 hover:bg-blue-600' : 'bg-transparent'}`}
                        style={{ height: `${height}px` }}
                        onMouseEnter={(e) => {
                          const rect = e.currentTarget.getBoundingClientRect();
                          setHoveredBar({ hour: item.hour, count: item.count, x: rect.left + rect.width / 2, y: rect.top - 10 });
                        }}
                        onMouseLeave={() => setHoveredBar(null)}
                      />
                    </div>
                    <div className="text-[10px] text-black/40 dark:text-white/40 mt-1 text-center w-full">{hourNumber}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* ── Recent Activity Table ── */}
      <div
        ref={tableSectionRef}
        className="rounded-lg border border-black/[0.06] dark:border-white/[0.06] bg-white dark:bg-neutral-800"
      >
        <div className="px-6 py-4 border-b border-black/[0.06] dark:border-white/[0.06]">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div>
              <h3 className="font-medium text-black/85 dark:text-white/85">Recent Node Executions</h3>
              <p className="text-sm text-black/60 dark:text-white/60 mt-0.5">
                {total.toLocaleString()} records
                {hasActiveFilters && ' (filtered)'}
                {hasTableDateRange && tableDateRange && (
                  <span className="ml-1 text-blue-600 dark:text-blue-400">
                    ·{' '}
                    {tableDateRange.start === tableDateRange.end
                      ? tableDateRange.start
                      : `${tableDateRange.start} → ${tableDateRange.end}`}
                  </span>
                )}
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setCursorStack([undefined]);
                  fetchTablePage(undefined, effectiveTableTimeRange);
                }}
                disabled={recordsLoading}
                className="h-8 w-8 p-0"
                title="Refresh table"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${recordsLoading ? 'animate-spin' : ''}`} />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setFiltersOpen((v) => !v)}
                className={`flex items-center gap-2 ${activeFilterCount > 0 ? 'border-blue-400 text-blue-600 dark:text-blue-400' : ''}`}
              >
                <Filter className="w-3.5 h-3.5" />
                Filters
                {activeFilterCount > 0 && (
                  <span className="flex items-center justify-center rounded-full bg-blue-500 text-white text-[10px] w-4 h-4 font-bold">
                    {activeFilterCount}
                  </span>
                )}
                {filtersOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              </Button>
            </div>
          </div>

          {filtersOpen && (
            <div className="mt-4 pt-4 border-t border-black/[0.06] dark:border-white/[0.06]">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {/* Date range picker */}
                <div>
                  <label className="block text-xs text-black/50 dark:text-white/50 mb-1">Date Range</label>
                  <DateRangePicker value={tableDateRange} onChange={setTableDateRange} />
                </div>

                {/* User email */}
                <div>
                  <label className="block text-xs text-black/50 dark:text-white/50 mb-1">User Email</label>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Exact email…"
                      value={filterUserEmail}
                      onChange={(e) => setFilterUserEmail(e.target.value)}
                      className="w-full px-3 py-1.5 text-sm rounded-md border border-black/[0.12] dark:border-white/[0.12]
                        bg-transparent text-black/85 dark:text-white/85 placeholder-black/40 dark:placeholder-white/40
                        focus:outline-none focus:ring-2 focus:ring-blue-500/40"
                    />
                    {filterUserEmail && (
                      <button
                        onClick={() => setFilterUserEmail('')}
                        className={[
                          'absolute right-2 top-1/2 -translate-y-1/2',
                          'text-black/40 hover:text-black/70 dark:text-white/40 dark:hover:text-white/70',
                        ].join(' ')}
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-xs text-black/50 dark:text-white/50 mb-1">Node Type</label>
                  <SearchableSelect
                    value={filterClassType}
                    onChange={setFilterClassType}
                    options={filterOptions.classTypes}
                    placeholder="Search node types…"
                    allLabel="All node types"
                  />
                </div>

                <div>
                  <label className="block text-xs text-black/50 dark:text-white/50 mb-1">Model</label>
                  <SearchableSelect
                    value={filterModel}
                    onChange={setFilterModel}
                    options={filterOptions.models}
                    placeholder="Search models…"
                    allLabel="All models"
                  />
                </div>
              </div>

              {(hasActiveFilters || hasTableDateRange) && (
                <button onClick={clearFilters} className="mt-2 text-xs text-blue-600 dark:text-blue-400 hover:underline">
                  Clear all filters
                </button>
              )}
            </div>
          )}
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-black/[0.02] dark:bg-white/[0.02] border-b border-black/[0.06] dark:border-white/[0.06]">
              <tr>
                <th className="text-left px-6 py-3 text-xs font-medium text-black/50 dark:text-white/50 uppercase tracking-wider">
                  User
                </th>
                <th className="text-left px-6 py-3 text-xs font-medium text-black/50 dark:text-white/50 uppercase tracking-wider">
                  Node Type
                </th>
                <th className="text-left px-6 py-3 text-xs font-medium text-black/50 dark:text-white/50 uppercase tracking-wider">
                  Model
                </th>
                <th className="text-left px-6 py-3 text-xs font-medium text-black/50 dark:text-white/50 uppercase tracking-wider">
                  Executed
                </th>
                <th className="text-left px-6 py-3 text-xs font-medium text-black/50 dark:text-white/50 uppercase tracking-wider">
                  Parameters
                </th>
                <th className="text-left px-6 py-3 text-xs font-medium text-black/50 dark:text-white/50 uppercase tracking-wider">
                  Workflow
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-neutral-800 divide-y divide-black/[0.06] dark:divide-white/[0.06]">
              {recordsLoading ? (
                <tr>
                  <td colSpan={6} className="py-16">
                    <div className="flex items-center justify-center">
                      <Spinner />
                    </div>
                  </td>
                </tr>
              ) : records.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-sm text-black/40 dark:text-white/40">
                    No records found{hasActiveFilters ? ' matching your filters' : ''}.
                  </td>
                </tr>
              ) : (
                records.map((record: NodeTrackingRecord, idx: number) => (
                  <tr
                    key={`${record.prompt_id}-${record.timestamp}-${record.class_type}-${idx}`}
                    className="hover:bg-black/[0.01] dark:hover:bg-white/[0.02]"
                  >
                    <td className="px-6 py-4 text-sm">
                      <div className="flex flex-col">
                        <span className="text-black/70 dark:text-white/70 font-medium">{record.user_email}</span>
                        <span className="text-xs text-black/40 dark:text-white/40 font-mono">{record.user_id}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <Badge variant="outline" className="font-mono text-xs">
                        {record.class_type}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {record.inputs?.model ? (
                        <span className="text-black/60 dark:text-white/60 text-xs font-mono">{String(record.inputs.model)}</span>
                      ) : (
                        <span className="text-black/30 dark:text-white/30">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <div className="flex flex-col">
                        <span className="text-black/60 dark:text-white/60">{formatRelativeTime(record.timestamp)}</span>
                        <span className="text-xs text-black/40 dark:text-white/40">{formatDate(record.timestamp)}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelectedPayload(record.inputs);
                          setIsModalOpen(true);
                        }}
                        className="h-8 px-2 text-xs hover:bg-black/5 dark:hover:bg-white/5"
                      >
                        <Eye className="w-3 h-3 mr-1" />
                        {Object.keys(record.inputs).length} params
                      </Button>
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {record.workflow_json ? (
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedPayload(
                                typeof record.workflow_json === 'string'
                                  ? JSON.parse(record.workflow_json)
                                  : record.workflow_json,
                              );
                              setIsModalOpen(true);
                            }}
                            className="h-8 px-2 text-xs hover:bg-black/5 dark:hover:bg-white/5"
                          >
                            <Eye className="w-3 h-3 mr-1" />
                            View
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              const raw =
                                typeof record.workflow_json === 'string'
                                  ? record.workflow_json
                                  : JSON.stringify(record.workflow_json, null, 2);
                              copyJson(raw, `${record.timestamp}-${record.user_id}`);
                            }}
                            className="h-8 px-2 text-xs hover:bg-black/5 dark:hover:bg-white/5"
                          >
                            {copiedKey === `${record.timestamp}-${record.user_id}` ? (
                              <Check className="w-3 h-3 text-green-500" />
                            ) : (
                              <Copy className="w-3 h-3" />
                            )}
                          </Button>
                        </div>
                      ) : (
                        <span className="text-black/30 dark:text-white/30">—</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination — only show buttons that are actionable */}
        {(canGoBack || hasNextPage) && (
          <div className="px-6 py-4 border-t border-black/[0.06] dark:border-white/[0.06] flex items-center justify-between">
            <p className="text-sm text-black/50 dark:text-white/50">
              Page <span className="font-medium text-black/70 dark:text-white/70">{currentPage}</span>
              {' · '}
              <span className="font-medium text-black/70 dark:text-white/70">{total.toLocaleString()}</span> records
            </p>
            <div className="flex items-center gap-2">
              {canGoBack && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={goBack}
                  disabled={recordsLoading}
                  className="flex items-center gap-1.5 h-8 px-3"
                >
                  <ChevronLeft className="w-3.5 h-3.5" />
                  Previous
                </Button>
              )}
              {hasNextPage && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={goNext}
                  disabled={recordsLoading}
                  className="flex items-center gap-1.5 h-8 px-3"
                >
                  Next
                  <ChevronRight className="w-3.5 h-3.5" />
                </Button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Tooltip */}
      {hoveredBar && (
        <div
          className="fixed pointer-events-none z-50 bg-black/90 text-white text-xs px-2 py-1 rounded shadow-lg transform -translate-x-1/2"
          style={{ left: hoveredBar.x, top: hoveredBar.y }}
        >
          {hoveredBar.hour}: {hoveredBar.count} execution{hoveredBar.count !== 1 ? 's' : ''}
        </div>
      )}

      {/* JSON Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-black/[0.06] dark:border-white/[0.06]">
              <h3 className="text-lg font-medium text-black/85 dark:text-white/85">Node Parameters</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setIsModalOpen(false);
                  setSelectedPayload(null);
                }}
                className="h-8 w-8 p-0"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="p-6 overflow-auto max-h-[calc(80vh-120px)]">
              <pre
                className={[
                  'bg-black/[0.02] dark:bg-white/[0.05] rounded-lg p-4 text-sm font-mono',
                  'text-black/85 dark:text-white/85 overflow-x-auto whitespace-pre-wrap',
                ].join(' ')}
              >
                {JSON.stringify(selectedPayload, null, 2)}
              </pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
